<template>
  <div class="theater-subscription-cart">
    <div class="cart-header">
      <h3>{{ $t('tsCartTitle') }}</h3>
    </div>

    <div v-if="!subscription" class="empty-cart">
      <div class="empty-cart-icon">
        <v-icon size="48" color="#ccc">mdi-ticket-outline</v-icon>
      </div>
      <p>{{ $t('tsCartEmpty') }}</p>
      <p class="empty-cart-hint">{{ $t('tsCartEmptyHint') }}</p>
    </div>

    <div v-else class="cart-content">
      <div class="selected-subscription">
        <div class="subscription-info">
          <h4>{{ subscription.name }}</h4>
          <div class="subscription-details">
            <div class="validity">
              <strong>{{ $t('tsCartValidLabel') }}</strong>
              {{ formatDate(subscription.valid_from) }} -
              {{ formatDate(subscription.valid_to) }}
            </div>
            <div v-if="subscription.venue" class="venue">
              <strong>{{ $t('tsCartVenueLabel') }}</strong>
              {{ subscription.venue.name }}
            </div>
          </div>
        </div>

        <div class="subscription-price">
          <div class="price-amount">{{ formatPrice(subscription.price) }}</div>
        </div>
      </div>
      <!-- <div class="promo-code-section">
        <v-text-field
          :label="$t('tsCartPromoLabel')"
          outlined
          dense
          hide-details
          class="promo-code-input"
        ></v-text-field>
      </div> -->

      <div v-for="(subscriber, index) in subscribers" :key="subscriber.id" class="subscriber-summary">
        <strong>{{ index + 1 }}. {{ subscriber.firstName }} {{ subscriber.lastName }}</strong>
        <div>{{ subscriber.seat ? subscriber.seat.replace(/Section-/g, '').replace(/_Row-/g, ' / ').replace(/_Seat-/g, ' / ') : $t(isGeneralAdmission ? 'tsAdmissionGeneral' : 'tsChooseSubscriberSeat') }}</div>
      </div>
      <div class="cart-summary">
        <div class="summary-row">
          <span>{{ $t('tsCartPriceLabel') }}</span>
          <span>{{ formatPrice(getTotalPrice()) }}</span>
        </div>
        <div v-if="selectedSeatIds && selectedSeatIds.length" class="summary-row">
          <span>{{ $t('tsCartSeatLabel') }}</span>
          <span class="seat-list">{{ selectedSeatIds.length }}</span>
        </div>
        <div v-else-if="isGeneralAdmission" class="summary-row">
          <span>{{ $t('tsCartSeatLabel') }}</span>
          <span class="seat-list">{{ $t('tsAdmissionGeneral') }}</span>
        </div>
        <div class="summary-row total">
          <span>{{ $t('tsCartTotalLabel') }}</span>
          <span>{{ formatPrice(getTotalPrice()) }}</span>
        </div>
      </div>

      <div v-if="step === 1 || step === 2" class="cart-actions">
        <template v-if="step === 1">
          <v-btn
            v-if="showNext"
            class="action-button"
            color="primary"
            size="large"
            block
            :disabled="disabled"
            @click="$emit('next')"
          >
            {{ $t('tsCartContinue') }}
          </v-btn>
          <v-btn
            v-else
            class="action-button"
            color="primary"
            size="large"
            block
            disabled
          >
            {{ $t('tsCartLoginRequired') }}
          </v-btn>
        </template>
        <template v-if="step === 2">
          <v-btn
            class="action-button"
            color="primary"
            size="large"
            block
            :disabled="disabled"
            @click="$emit('complete')"
          >
            {{ $t('tsCartComplete') }}
          </v-btn>
        </template>
      </div>

      <div class="cart-note">
        <p>
          <strong>{{ $t('tsCartNoteLabel') }}</strong>
          {{ $t('tsCartNoteLine1') }}
        </p>
        <p>{{ $t('tsCartNoteLine2') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'theater-subscription-cart',
  props: {
    subscribers: { type: Array, default: () => [] },
    subscription: {
      type: Object,
      default: null,
    },
    showNext: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: 1,
    },
    selectedSeatIds: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    isGeneralAdmission() {
      return !!(this.subscription && this.subscription.venue && !this.venueHasSeatMap(this.subscription.venue))
    },
  },
  methods: {
    venueHasSeatMap(venue) {
      return !!(venue && venue.seats && venue.seats.length && venue.predefinedSections)
    },
    formatPrice(price) {
      return `€${parseFloat(price).toFixed(2)}`
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    getTotalPrice() {
      if (!this.subscription) return 0

      const count = this.subscribers.length

      return parseFloat(this.subscription.price) * count
    },
  },
}
</script>

<style scoped>
.theater-subscription-cart {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  height: fit-content;
  position: sticky;
  top: 2rem;
  font-family: 'Raleway', sans-serif;
  @media screen and (min-width: 993px) {
    margin-right: 1.5rem;
  }
  margin-top: 1.5rem;
}

.cart-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.cart-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #212121;
}

.empty-cart {
  text-align: center;
  padding: 2rem 1rem;
  color: #666;
}

.empty-cart-icon {
  margin-bottom: 1rem;
}

.empty-cart p {
  margin: 0.5rem 0;
}

.empty-cart-hint {
  font-size: 14px;
  color: #999;
}

.cart-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.selected-subscription {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.subscription-info {
  flex: 1;
}

.subscription-info h4 {
  margin: 0 0 0.5rem 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.subscription-details {
  font-size: 14px;
  color: #666;
  font-family: 'Source Sans Pro', sans-serif;
}

.subscription-details > div {
  margin-bottom: 0.25rem;
}

.subscription-details > div:last-child {
  margin-bottom: 0;
}

.subscription-price {
  text-align: right;
  margin-left: 1rem;
}

.price-amount {
  font-size: 24px;
  font-weight: 700;
  color: #212121;
  line-height: 1;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.promo-code-section {
  padding-top: 8px;
}

.promo-code-input {
  border-radius: 24px;
}

.cart-summary {
  border-top: 1px solid #e0e0e0;
  padding-top: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 16px;
  font-family: 'Source Sans Pro', sans-serif;
}

.summary-row.total {
  font-size: 18px;
  font-weight: 700;
  color: #212121;
  font-family: 'Raleway', sans-serif;
  border-top: 1px solid #e0e0e0;
  padding-top: 16px;
  margin-top: 16px;
}
.seat-list {
  text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.cart-actions {
  margin-top: 1rem;
}

.action-button {
  border-radius: 24px !important;
  text-transform: uppercase !important;
  font-weight: 700 !important;
  height: 48px !important;
}

.cart-note {
  margin-top: 1rem;
  padding: 16px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  font-family: 'Source Sans Pro', sans-serif;
}

.cart-note p {
  margin: 0.25rem 0;
}

.cart-note p:first-child {
  margin-top: 0;
}

.cart-note p:last-child {
  margin-bottom: 0;
}

@media (max-width: 992px) {
  .theater-subscription-cart {
    position: static;
    margin-top: 1.5rem;
    margin-left: 0;
    margin-right: 0;
    border-radius: 0;
    border-left: none;
    border-right: none;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
  }

  .selected-subscription {
    flex-direction: column;
    align-items: flex-start;
  }

  .subscription-price {
    margin-left: 0;
    margin-top: 1rem;
    align-self: flex-end;
  }
}

.subscriber-summary { padding-bottom: 14px; border-bottom: 1px solid #edf0f2; font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; }
.subscriber-summary strong { display: block; font-size: 14px; color: #303941; }
.subscriber-summary div { color: #67737b; }
.theater-subscription-cart { margin: 0; padding: 24px; border-radius: 12px; width: 100%; border: 1px solid #e1e6ea; }
.cart-header { margin-bottom: 20px; padding-bottom: 16px; }
.cart-content { gap: 16px; }
.selected-subscription { background: #f7f9fb; padding: 14px; border: 0; border-radius: 8px; }
.price-amount { font-size: 20px; color: #0277bd; }
.subscription-info h4 { font-size: 15px; }
.cart-note { margin-top: 0; padding: 12px; background: #f7f9fb; border: 0; font-size: 12px; line-height: 1.5; }
.cart-actions { margin-top: 0; }
@media (max-width: 992px) { .theater-subscription-cart { margin: 0; width: 100%; border: 1px solid #e1e6ea; border-radius: 12px; } }
</style>
